import { Trans } from "react-i18next";
import i18nPackage from "../i18n/i18n";
import { EditQuizStep } from "../editQuiz";
import { EnabledProctoringOptions, Lms } from "@verity/api-client/types";
import { VerityInstituteSettings } from "../types/common";
import { EditQuizPageProps } from "../editQuiz/editQuiz";
import { ProctoringOptionProfileV2 } from "@verity/proctoring-option/types";
import { isAutomatedProctoring } from "../editQuiz/editQuizHelper";
import type { ProctoringOption } from "@verity/proctoring-option/types";

export type SetupStepCreateId =
	| "highlightCheckbox" // verity checkbox
	| "editQuiz" // editQuiz.tsx step
	| "saveQuiz" // LMS save button
	| "editQuizError" // editQuiz save error
	| "createQuizButton" // LMS create quiz button
	| "chooseQuizEngine" // For canvas which requires choosing between classic/new quiz.
	// canvas needs user to confirm. Also using it bb original because they need to select the quiz in the dropdown
	| "confirmQuizEngine"
	| "initialSaveQuiz" // for LMS that requires saving lms quiz before verity is injected
	| "editLmsQuiz" // for lms that requires user to edit lms quiz settings
	| "openLmsQuizOptions" // for lms that requires user to open a dropdown to edit or do stuff
	| "initialQuizContinue" // moodle 4.x + directs them to a "continue" page after initial save. this is bb original's question step ??. this is bb ultra's "View Verity Settings" step
	| "initialQuizDetails" // bb original has the user fill in quiz details 3 times. this is the first time where they fill in title
	// bb original fallback if we couldn't fetch content_id. means we need user to do an extra step and create a test first. createQuizButton is another create button after they create a test
	| "fallbackCreateQuiz";
export type SetupStepTakeId =
	| "clickQuiz"
	| "previewButton"
	| "startVerity"
	| "endPreview"
	| "noQuestion"
	| "openLmsQuizOptions" // bb ultra: open the visibility dropdown before selecting "Visible to Students"
	| "makeVisible"
	| "studentPreview"
	| "confirmStudentPreview";
export type SetupStepViewId =
	| "viewResultLms"
	| "viewResultVerity"
	| "finishLMSReview"
	| "finishLMSReview2" // bb original has 2 review pages. it shows after you click ok on finishLmsReview. they look exactly the same
	| "openLmsQuizOptions"
	| "editLmsQuiz";

export type SetupStepId = SetupStepCreateId | SetupStepTakeId | SetupStepViewId;

export type TextConfig<TId extends SetupStepId = SetupStepId> = {
	id: TId;
	title: React.ReactNode;
	description: React.ReactNode;
};

type CreateTextConfig = TextConfig<SetupStepCreateId>;
type TakeTextConfig = TextConfig<SetupStepTakeId>;
type ViewTextConfig = TextConfig<SetupStepViewId>;

type SetupGuideConfig = {
	create: CreateTextConfig[];
	take: TakeTextConfig[];
	view: ViewTextConfig[];
};

const BOLD_TEXT = {
	i18n: i18nPackage,
	components: { b: <strong /> },
};

const HIGHLIGHT_CHECKBOX_TEXT: CreateTextConfig = {
	id: "highlightCheckbox",
	title: i18nPackage.t("quickSetupGuide.create.checkboxTitle"),
	description: i18nPackage.t("quickSetupGuide.create.checkboxDescription"),
};

export const SAVE_VERITY_ERROR_TEXT: CreateTextConfig = {
	id: "editQuizError",
	title: i18nPackage.t("quickSetupGuide.create.editQuizSaveErrorTitle"),
	description: i18nPackage.t("quickSetupGuide.create.editQuizSaveErrorDescription"),
};

// Generic step for guide. But editQuiz will have substeps -> see getEditQuizGuideState for more specific text
const EDIT_QUIZ_TEXT: CreateTextConfig = {
	id: "editQuiz",
	title: i18nPackage.t("quickSetupGuide.create.editQuiz.title"),
	description: i18nPackage.t("quickSetupGuide.create.editQuiz.descriptionFallback"),
};

type GetEditQuizGuideStateParams = {
	curStep: EditQuizStep;
	enabledProctoringOptions: EnabledProctoringOptions;
	verityInstituteSettings: VerityInstituteSettings;
	proctoringAssessmentConfiguration: NonNullable<EditQuizPageProps["proctoringAssessmentConfiguration"]>;
	enforcedProfilesOnly: boolean;
	profiles: ProctoringOptionProfileV2[];
	activeProfile?: ProctoringOptionProfileV2;
	proctoringOption: NonNullable<EditQuizPageProps["proctoringOption"]>;
};

/**
 * The guide walks the admin to one automated-proctoring platform. The extension is preferred because
 * it is the more capable of the two; the browser stands in for institutions that do not allow it.
 */
function guidedPlatformOf(verityInstituteSettings: VerityInstituteSettings): "extension" | "browser" | undefined {
	if (verityInstituteSettings.allowExtensionPlatform) return "extension";
	return verityInstituteSettings.allowBrowserPlatform ? "browser" : undefined;
}

// Logic is here because this is a tsx file and editQuizHelper is a ts file
export function getEditQuizGuideState({
	curStep,
	enabledProctoringOptions,
	verityInstituteSettings,
	proctoringAssessmentConfiguration,
	enforcedProfilesOnly,
	profiles,
	activeProfile,
	proctoringOption,
}: GetEditQuizGuideStateParams): {
	description: React.ReactNode;
	/**Undefined isNextEnabled means to use default logic */
	isNextEnabled?: boolean;
	/**What should be highlighted.
	 * Next = next button.
	 * Default = Step logic takes over .
	 * Fallback = highlight fallback element
	 *
	 * The types below -> can probably make more elegant
	 * Custom-config = highlight custom configuration element
	 * Live-check-in = highlight live check-in element
	 *  */
	highlightTarget?: "next" | "default" | "fallback" | "custom-config" | "live-check-in";
} {
	// Try to force user to choose automated proctoring
	// Fallback to live-check in
	// Otherwise, they can choose any method
	if (curStep === "method") {
		const automatedAllowed = enabledProctoringOptions.proctoringMethod.includes("automated");

		if (automatedAllowed) {
			const automatedSelected = isAutomatedProctoring(proctoringAssessmentConfiguration);

			return {
				description: (
					<Trans
						i18nKey={`quickSetupGuide.create.editQuiz.${automatedSelected ? "methodNext" : "methodDescription"}`}
						{...BOLD_TEXT}
					/>
				),
				isNextEnabled: automatedSelected,
				highlightTarget: automatedSelected ? "next" : "default",
			};
		}

		const liveCheckInAllowed = enabledProctoringOptions.proctoringMethod.includes("pop-in");

		if (liveCheckInAllowed) {
			const liveCheckInSelected = proctoringAssessmentConfiguration.isLivePopIn;
			return {
				description: (
					<Trans
						i18nKey={`quickSetupGuide.create.editQuiz.${liveCheckInSelected ? "methodLiveCheckInNext" : "methodLiveCheckIn"}`}
						{...BOLD_TEXT}
					/>
				),
				isNextEnabled: liveCheckInSelected,
				highlightTarget: liveCheckInSelected ? "next" : "live-check-in",
			};
		}

		// TODO add route for live check in with post review??

		// Assuming method is pre-selected
		return {
			description: i18nPackage.t("quickSetupGuide.create.editQuiz.methodFallback"),
			highlightTarget: "next",
		};
	}
	// Try to get them to choose profile they created in "Create Proctoring Profile Step" or a profile
	// on the guided platform. If only lockdown profiles, force them to choose "custom configuration".
	// If enforced profile and only lockdown profiles, can't do anything about that so let them choose any profile
	else if (curStep === "profile") {
		const guidedPlatform = guidedPlatformOf(verityInstituteSettings);
		const usesGuidedPlatform = (option: Partial<ProctoringOption>) =>
			guidedPlatform === "browser" ? Boolean(option.isBrowser) : Boolean(option.isExtension);
		const hasGuidedProfile =
			guidedPlatform !== undefined &&
			profiles.some((p) => p.type !== "preset" && usesGuidedPlatform(p.proctoringOption));

		if (hasGuidedProfile) {
			const guidedProfileChosen = Boolean(activeProfile && usesGuidedPlatform(activeProfile.proctoringOption));
			const keySuffix = guidedPlatform === "browser" ? "Browser" : "";

			return {
				description: (
					<Trans
						i18nKey={`quickSetupGuide.create.editQuiz.profile${keySuffix}${guidedProfileChosen ? "Next" : "Description"}`}
						{...BOLD_TEXT}
					/>
				),
				isNextEnabled: guidedProfileChosen,
				highlightTarget: guidedProfileChosen ? "next" : "default",
			};
		}

		// If enforced profile, and they only have lockdown profiles
		if (enforcedProfilesOnly) {
			return {
				description: i18nPackage.t("quickSetupGuide.create.editQuiz.profileFallback"),
				highlightTarget: "next",
			};
		}

		// They only have lockdown profiles so force them to choose custom config (activeProfile === undefined)
		return {
			description: (
				<Trans
					i18nKey={`quickSetupGuide.create.editQuiz.${activeProfile ? "profileNoExtensionFallback" : "profileNoExtensionNext"}`}
					{...BOLD_TEXT}
				/>
			),
			isNextEnabled: !activeProfile,
			highlightTarget: activeProfile ? "custom-config" : "next",
		};
	}

	// Force user to choose the guided platform
	// Fallback to whatever the institution allows
	else if (curStep === "platform") {
		const guidedPlatform = guidedPlatformOf(verityInstituteSettings);

		if (guidedPlatform === undefined) {
			return {
				highlightTarget: "next",
				description: i18nPackage.t("quickSetupGuide.create.editQuiz.platformFallback"),
			};
		}

		const guidedPlatformChosen = Boolean(
			guidedPlatform === "browser" ? proctoringOption.isBrowser : proctoringOption.isExtension,
		);
		const keySuffix = guidedPlatform === "browser" ? "Browser" : "";

		return {
			description: (
				<Trans
					i18nKey={`quickSetupGuide.create.editQuiz.platform${keySuffix}${guidedPlatformChosen ? "Next" : "Description"}`}
					{...BOLD_TEXT}
				/>
			),
			isNextEnabled: guidedPlatformChosen,
			highlightTarget: guidedPlatformChosen ? "next" : "default",
		};
	} else if (curStep === "confirmation") {
		return {
			highlightTarget: "next",
			description: SAVE_EDIT_QUIZ_PROMPT,
		};
	}

	return {
		highlightTarget: "default",
		description: i18nPackage.t(`quickSetupGuide.create.editQuiz.configurationDescription`),
	};
}

// Review step of edit quiz
export const SAVE_EDIT_QUIZ_PROMPT = (
	<Trans i18nKey="quickSetupGuide.create.editQuiz.reviewDescription" {...BOLD_TEXT} />
);

// Saving in the LMS
const SAVE_QUIZ_TITLE = i18nPackage.t("quickSetupGuide.create.lmsSaveTitle");

const VIEW_RESULT_LMS_TEXT: ViewTextConfig = {
	id: "viewResultLms",
	title: i18nPackage.t("quickSetupGuide.view.viewResultsLmsTitle"),
	description: <Trans i18nKey="quickSetupGuide.view.viewResultsLmsDescription" {...BOLD_TEXT} />,
};

const VIEW_REPORT_TEXT: ViewTextConfig = {
	id: "viewResultVerity",
	title: i18nPackage.t("quickSetupGuide.view.viewReportTitle"),
	description: <Trans i18nKey="quickSetupGuide.view.viewReportDescription" {...BOLD_TEXT} />,
};

const CANVAS_SETUP_TEXT: SetupGuideConfig = {
	create: [
		{
			id: "createQuizButton",
			title: i18nPackage.t("quickSetupGuide.create.canvas.createQuizTitle"),
			description: <Trans {...BOLD_TEXT} i18nKey={"quickSetupGuide.create.canvas.createQuizDescription"} />,
		},
		{
			id: "chooseQuizEngine",
			title: i18nPackage.t("quickSetupGuide.create.canvas.chooseQuizEngineTitle"),
			description: <Trans {...BOLD_TEXT} i18nKey={"quickSetupGuide.create.canvas.chooseQuizEngineDescription"} />,
		},
		{
			id: "confirmQuizEngine",
			title: i18nPackage.t("quickSetupGuide.create.canvas.quizEngineConfirmTitle"),
			description: <Trans i18nKey={"quickSetupGuide.create.canvas.quizEngineConfirmDescription"} {...BOLD_TEXT} />,
		},
		{
			...HIGHLIGHT_CHECKBOX_TEXT,
			title: i18nPackage.t("quickSetupGuide.create.canvas.checkboxTitle"),
		},
		EDIT_QUIZ_TEXT,
		{
			id: "saveQuiz",
			title: SAVE_QUIZ_TITLE,
			description: <Trans i18nKey="quickSetupGuide.create.canvas.lmsSaveDescription" {...BOLD_TEXT} />,
		},
	],
	take: [
		{
			id: "previewButton",
			title: i18nPackage.t("quickSetupGuide.take.canvas.previewButtonTitle"),
			description: <Trans i18nKey="quickSetupGuide.take.canvas.previewButtonDescription" {...BOLD_TEXT} />,
		},
		{
			id: "startVerity",
			title: i18nPackage.t("quickSetupGuide.take.canvas.previewButtonYes"),
			description: <Trans i18nKey="quickSetupGuide.take.canvas.previewButtonYesDescription" {...BOLD_TEXT} />,
		},
	],
	view: [VIEW_RESULT_LMS_TEXT, VIEW_REPORT_TEXT],
};

const D2L_SETUP_TEXT: SetupGuideConfig = {
	create: [
		{
			id: "createQuizButton",
			title: i18nPackage.t("quickSetupGuide.create.d2l.createQuizTitle"),
			description: <Trans {...BOLD_TEXT} i18nKey={"quickSetupGuide.create.d2l.createQuizDescription"} />,
		},
		HIGHLIGHT_CHECKBOX_TEXT,
		EDIT_QUIZ_TEXT,
		{
			id: "saveQuiz",
			title: SAVE_QUIZ_TITLE,
			description: <Trans i18nKey="quickSetupGuide.create.d2l.lmsSaveDescription" {...BOLD_TEXT} />,
		},
	],
	take: [
		{
			id: "clickQuiz",
			title: i18nPackage.t("quickSetupGuide.take.d2l.dropdownButtonTitle"),
			description: <Trans i18nKey="quickSetupGuide.take.d2l.dropdownButtonTitleDescription" {...BOLD_TEXT} />,
		},
		{
			id: "previewButton",
			title: i18nPackage.t("quickSetupGuide.take.d2l.previewButtonTitle"),
			description: <Trans i18nKey="quickSetupGuide.take.d2l.previewButtonDescription" {...BOLD_TEXT} />,
		},
		{
			id: "startVerity",
			title: i18nPackage.t("quickSetupGuide.take.d2l.startQuizButton"),
			description: <Trans i18nKey="quickSetupGuide.take.d2l.startQuizButtonDescription" {...BOLD_TEXT} />,
		},
		{
			id: "endPreview",
			title: i18nPackage.t("quickSetupGuide.take.d2l.endPreviewButton"),
			description: <Trans i18nKey="quickSetupGuide.take.d2l.endPreviewButtonDescription" {...BOLD_TEXT} />,
		},
	],
	view: [VIEW_RESULT_LMS_TEXT, VIEW_REPORT_TEXT],
};

const MOODLE_4p1_SETUP_TEXT: SetupGuideConfig = {
	create: [
		{
			id: "initialSaveQuiz",
			title: i18nPackage.t("quickSetupGuide.create.moodle.lmsInitialSaveTitle"),
			description: <Trans i18nKey="quickSetupGuide.create.moodle.lmsInitialSaveDescription" {...BOLD_TEXT} />,
		},
		{
			id: "initialQuizContinue",
			title: i18nPackage.t("quickSetupGuide.create.moodle.continueTitle"),
			description: <Trans i18nKey="quickSetupGuide.create.moodle.continueDescription" {...BOLD_TEXT} />,
		},
		{
			id: "editLmsQuiz",
			title: i18nPackage.t("quickSetupGuide.create.moodle.settingsTitle"),
			description: <Trans i18nKey="quickSetupGuide.create.moodle.settingsDescription" {...BOLD_TEXT} />,
		},
		HIGHLIGHT_CHECKBOX_TEXT,
		EDIT_QUIZ_TEXT,
		{
			id: "saveQuiz",
			title: SAVE_QUIZ_TITLE,
			description: <Trans i18nKey="quickSetupGuide.create.moodle.lmsSaveDescription" {...BOLD_TEXT} />,
		},
	],
	take: [
		{
			id: "noQuestion",
			title: i18nPackage.t("quickSetupGuide.take.moodle.noQuestionTitle"),
			description: <Trans i18nKey="quickSetupGuide.take.moodle.noQuestionDescription" {...BOLD_TEXT} />,
		},
		{
			id: "startVerity",
			title: i18nPackage.t("quickSetupGuide.take.moodle.startQuizButton"),
			description: <Trans i18nKey="quickSetupGuide.take.moodle.startQuizButtonDescription" {...BOLD_TEXT} />,
		},
	],
	view: [
		{
			id: "finishLMSReview",
			title: i18nPackage.t("quickSetupGuide.view.moodle.finishLMSReview"),
			description: <Trans i18nKey="quickSetupGuide.view.moodle.finishLMSReviewDescription" {...BOLD_TEXT} />,
		},
		VIEW_RESULT_LMS_TEXT,
		VIEW_REPORT_TEXT,
	],
};

const MOODLE_3p9_SETUP_TEXT: SetupGuideConfig = {
	create: [
		{
			id: "initialSaveQuiz",
			title: i18nPackage.t("quickSetupGuide.create.moodle.lmsInitialSaveTitle"),
			description: <Trans i18nKey="quickSetupGuide.create.moodle.lmsInitialSaveDescription" {...BOLD_TEXT} />,
		},
		{
			id: "openLmsQuizOptions",
			title: i18nPackage.t("quickSetupGuide.create.moodle.dropdownButtonTitle"),
			description: <Trans i18nKey="quickSetupGuide.create.moodle.dropdownButtonTitleDescription" {...BOLD_TEXT} />,
		},
		{
			id: "editLmsQuiz",
			title: i18nPackage.t("quickSetupGuide.create.moodle.settingsTitle"),
			description: <Trans i18nKey="quickSetupGuide.create.moodle.settingsDescription3p9" {...BOLD_TEXT} />,
		},
		HIGHLIGHT_CHECKBOX_TEXT,
		EDIT_QUIZ_TEXT,
		{
			id: "saveQuiz",
			title: SAVE_QUIZ_TITLE,
			description: <Trans i18nKey="quickSetupGuide.create.moodle.lmsSaveDescription" {...BOLD_TEXT} />,
		},
	],
	take: [
		{
			id: "noQuestion",
			title: i18nPackage.t("quickSetupGuide.take.moodle.noQuestionTitle"),
			description: <Trans i18nKey="quickSetupGuide.take.moodle.noQuestionDescription3p9" {...BOLD_TEXT} />,
		},
		{
			id: "startVerity",
			title: i18nPackage.t("quickSetupGuide.take.moodle.startQuizButton"),
			description: <Trans i18nKey="quickSetupGuide.take.moodle.startQuizButtonDescription3p9" {...BOLD_TEXT} />,
		},
	],
	view: [
		{
			id: "finishLMSReview",
			title: i18nPackage.t("quickSetupGuide.view.moodle.finishLMSReview"),
			description: <Trans i18nKey="quickSetupGuide.view.moodle.finishLMSReviewDescription" {...BOLD_TEXT} />,
		},
		VIEW_RESULT_LMS_TEXT,
		VIEW_REPORT_TEXT,
	],
};

const BB_ORIGINAL_TEXT: SetupGuideConfig = {
	create: [
		{
			id: "fallbackCreateQuiz",
			title: i18nPackage.t("quickSetupGuide.create.bbOriginal.fallbackCreateQuizTitle"),
			description: <Trans {...BOLD_TEXT} i18nKey={"quickSetupGuide.create.bbOriginal.fallbackCreateQuizDescription"} />,
		},
		{
			id: "chooseQuizEngine",
			title: i18nPackage.t("quickSetupGuide.create.bbOriginal.selectCreatedQuizTitle"),
			description: <Trans i18nKey="quickSetupGuide.create.bbOriginal.selectCreatedQuizDescription" {...BOLD_TEXT} />,
		},
		{
			id: "createQuizButton",
			title: i18nPackage.t("quickSetupGuide.create.bbOriginal.createQuizTitle"),
			description: <Trans {...BOLD_TEXT} i18nKey={"quickSetupGuide.create.bbOriginal.createQuizDescription"} />,
		},
		{
			id: "initialQuizDetails",
			title: i18nPackage.t("quickSetupGuide.create.bbOriginal.initialQuizDetailsTitle"),
			description: <Trans i18nKey="quickSetupGuide.create.bbOriginal.initialQuizDetailsDescription" {...BOLD_TEXT} />,
		},
		{
			id: "initialQuizContinue",
			title: i18nPackage.t("quickSetupGuide.create.bbOriginal.continueTitle"),
			description: <Trans i18nKey="quickSetupGuide.create.bbOriginal.continueDescription" {...BOLD_TEXT} />,
		},
		{
			id: "confirmQuizEngine",
			title: i18nPackage.t("quickSetupGuide.create.bbOriginal.selectCreatedQuizTitle"),
			description: <Trans i18nKey="quickSetupGuide.create.bbOriginal.selectCreatedQuizDescription" {...BOLD_TEXT} />,
		},
		{
			id: "initialSaveQuiz",
			title: i18nPackage.t("quickSetupGuide.create.bbOriginal.lmsInitialSaveTitle"),
			description: <Trans i18nKey="quickSetupGuide.create.bbOriginal.lmsInitialSaveDescription" {...BOLD_TEXT} />,
		},
		{
			id: "openLmsQuizOptions",
			title: i18nPackage.t("quickSetupGuide.create.bbOriginal.dropdownButtonTitle"),
			description: i18nPackage.t("quickSetupGuide.create.bbOriginal.dropdownButtonTitleDescription"),
		},
		{
			id: "editLmsQuiz",
			title: i18nPackage.t("quickSetupGuide.create.bbOriginal.settingsTitle"),
			description: <Trans i18nKey="quickSetupGuide.create.bbOriginal.settingsDescription" {...BOLD_TEXT} />,
		},
		HIGHLIGHT_CHECKBOX_TEXT,
		EDIT_QUIZ_TEXT,
		{
			id: "saveQuiz",
			title: SAVE_QUIZ_TITLE,
			description: <Trans i18nKey="quickSetupGuide.create.bbOriginal.lmsSaveDescription" {...BOLD_TEXT} />,
		},
	],
	take: [
		{
			id: "clickQuiz",
			title: i18nPackage.t("quickSetupGuide.take.bbOriginal.clickQuizTitle"),
			description: <Trans i18nKey="quickSetupGuide.take.bbOriginal.clickQuizDescription" {...BOLD_TEXT} />,
		},
		{
			id: "startVerity",
			title: i18nPackage.t("quickSetupGuide.take.bbOriginal.startQuizButton"),
			description: <Trans i18nKey="quickSetupGuide.take.bbOriginal.startQuizButtonDescription" {...BOLD_TEXT} />,
		},
	],
	view: [
		{
			id: "finishLMSReview",
			title: i18nPackage.t("quickSetupGuide.view.bbOriginal.finishLMSReview"),
			description: <Trans i18nKey="quickSetupGuide.view.bbOriginal.finishLMSReviewDescription" {...BOLD_TEXT} />,
		},
		{
			id: "finishLMSReview2",
			title: i18nPackage.t("quickSetupGuide.view.bbOriginal.finishLMSReview2"),
			description: <Trans i18nKey="quickSetupGuide.view.bbOriginal.finishLMSReview2Description" {...BOLD_TEXT} />,
		},
		{
			id: "openLmsQuizOptions",
			title: i18nPackage.t("quickSetupGuide.view.bbOriginal.openQuizOptionsTitle"),
			description: i18nPackage.t("quickSetupGuide.view.bbOriginal.openQuizOptionsDescription"),
		},
		{
			id: "editLmsQuiz",
			title: i18nPackage.t("quickSetupGuide.view.bbOriginal.settingsTitle"),
			description: <Trans i18nKey="quickSetupGuide.view.bbOriginal.settingsDescription" {...BOLD_TEXT} />,
		},
		VIEW_RESULT_LMS_TEXT,
		VIEW_REPORT_TEXT,
	],
};

const BB_ULTRA_TEXT: SetupGuideConfig = {
	create: [
		{
			id: "fallbackCreateQuiz",
			title: i18nPackage.t("quickSetupGuide.create.bbUltra.fallbackCreateQuizTitle"),
			description: (
				<Trans i18nKey={"quickSetupGuide.create.bbUltra.fallbackCreateQuizDescription"} i18n={i18nPackage} />
			),
		},
		{
			id: "createQuizButton",
			title: i18nPackage.t("quickSetupGuide.create.bbUltra.createQuizTitle"),
			description: <Trans {...BOLD_TEXT} i18nKey={"quickSetupGuide.create.bbUltra.createQuizDescription"} />,
		},
		{
			id: "chooseQuizEngine",
			title: i18nPackage.t("quickSetupGuide.create.bbUltra.chooseQuizEngineTitle"),
			description: <Trans i18nKey="quickSetupGuide.create.bbUltra.chooseQuizEngineDescription" {...BOLD_TEXT} />,
		},
		{
			id: "openLmsQuizOptions",
			title: i18nPackage.t("quickSetupGuide.create.bbUltra.openLmsQuizOptionsTitle"),
			description: <Trans i18n={i18nPackage} i18nKey="quickSetupGuide.create.bbUltra.openLmsQuizOptionsDescription" />,
		},
		{
			id: "editLmsQuiz",
			title: i18nPackage.t("quickSetupGuide.create.bbUltra.editLmsQuizTitle"),
			description: <Trans i18nKey="quickSetupGuide.create.bbUltra.editLmsQuizDescription" {...BOLD_TEXT} />,
		},
		{
			...HIGHLIGHT_CHECKBOX_TEXT,
			description: <Trans i18nKey="quickSetupGuide.create.bbUltra.checkboxDescription" {...BOLD_TEXT} />,
		},
		{
			id: "initialQuizContinue",
			title: i18nPackage.t("quickSetupGuide.create.bbUltra.initialQuizContinueTitle"),
			description: <Trans i18nKey="quickSetupGuide.create.bbUltra.initialQuizContinueDescription" {...BOLD_TEXT} />,
		},
		EDIT_QUIZ_TEXT,
		{
			id: "initialSaveQuiz",
			title: i18nPackage.t("quickSetupGuide.create.bbUltra.initialSaveQuizTitle"),
			description: <Trans i18nKey="quickSetupGuide.create.bbUltra.initialSaveQuizDescription" {...BOLD_TEXT} />,
		},
		{
			id: "saveQuiz",
			title: SAVE_QUIZ_TITLE,
			description: <Trans i18nKey="quickSetupGuide.create.bbUltra.lmsSaveDescription" {...BOLD_TEXT} />,
		},
	],
	take: [
		{
			id: "noQuestion",
			title: i18nPackage.t("quickSetupGuide.take.bbUltra.noQuestionTitle"),
			description: <Trans i18nKey="quickSetupGuide.take.bbUltra.noQuestionDescription" i18n={i18nPackage} />,
		},
		{
			id: "openLmsQuizOptions",
			title: i18nPackage.t("quickSetupGuide.take.bbUltra.openLmsQuizOptionsTitle"),
			description: <Trans i18nKey="quickSetupGuide.take.bbUltra.openLmsQuizOptionsDescription" {...BOLD_TEXT} />,
		},
		{
			id: "makeVisible",
			title: i18nPackage.t("quickSetupGuide.take.bbUltra.makeVisibleTitle"),
			description: <Trans i18nKey="quickSetupGuide.take.bbUltra.makeVisibleDescription" {...BOLD_TEXT} />,
		},
		{
			id: "endPreview",
			title: i18nPackage.t("quickSetupGuide.take.bbUltra.endPreviewTitle"),
			description: <Trans i18nKey="quickSetupGuide.take.bbUltra.endPreviewDescription" i18n={i18nPackage} />,
		},
		{
			id: "studentPreview",
			title: i18nPackage.t("quickSetupGuide.take.bbUltra.studentPreviewTitle"),
			description: <Trans i18nKey="quickSetupGuide.take.bbUltra.studentPreviewDescription" {...BOLD_TEXT} />,
		},
		{
			id: "confirmStudentPreview",
			title: i18nPackage.t("quickSetupGuide.take.bbUltra.confirmStudentPreviewTitle"),
			description: <Trans i18nKey="quickSetupGuide.take.bbUltra.confirmStudentPreviewDescription" {...BOLD_TEXT} />,
		},
		{
			id: "clickQuiz",
			title: i18nPackage.t("quickSetupGuide.take.bbUltra.clickQuizTitle"),
			description: <Trans i18nKey="quickSetupGuide.take.bbUltra.clickQuizDescription" {...BOLD_TEXT} />,
		},
		{
			id: "startVerity",
			title: i18nPackage.t("quickSetupGuide.take.bbUltra.startQuizButton"),
			description: <Trans i18nKey="quickSetupGuide.take.bbUltra.startQuizButtonDescription" {...BOLD_TEXT} />,
		},
	],
	view: [
		{
			id: "finishLMSReview",
			title: i18nPackage.t("quickSetupGuide.view.bbUltra.finishLMSReview"),
			description: <Trans i18nKey="quickSetupGuide.view.bbUltra.finishLMSReviewDescription" {...BOLD_TEXT} />,
		},
		{
			id: "openLmsQuizOptions",
			title: i18nPackage.t("quickSetupGuide.view.bbUltra.openQuizOptionsTitle"),
			description: <Trans i18nKey="quickSetupGuide.view.bbUltra.openQuizOptionsDescription" {...BOLD_TEXT} />,
		},
		{
			id: "editLmsQuiz",
			title: i18nPackage.t("quickSetupGuide.view.bbUltra.editLmsQuizTitle"),
			description: <Trans i18nKey="quickSetupGuide.view.bbUltra.editLmsQuizDescription" {...BOLD_TEXT} />,
		},
		VIEW_RESULT_LMS_TEXT,
		VIEW_REPORT_TEXT,
	],
};

export type ExtendedLms =
	Extract<Lms, "canvas" | "desire2learn"> | "blackboardUltra" | "blackboardOriginal" | "moodle3p9" | "moodle4p1";

export function getSetupTextByLMS(lms: ExtendedLms): SetupGuideConfig;
export function getSetupTextByLMS<K extends keyof SetupGuideConfig>(lms: ExtendedLms, step: K): SetupGuideConfig[K];

/**Gets setup text based on LMS. If a specific step is provided, returns only that step's configuration. */
export function getSetupTextByLMS(lms: ExtendedLms, step?: keyof SetupGuideConfig) {
	let config: SetupGuideConfig;
	switch (lms) {
		case "canvas":
			config = CANVAS_SETUP_TEXT;
			break;
		case "desire2learn":
			config = D2L_SETUP_TEXT;
			break;
		case "moodle3p9":
			config = MOODLE_3p9_SETUP_TEXT;
			break;
		case "moodle4p1":
			config = MOODLE_4p1_SETUP_TEXT;
			break;
		case "blackboardOriginal":
			config = BB_ORIGINAL_TEXT;
			break;
		case "blackboardUltra":
			config = BB_ULTRA_TEXT;
			break;
		default:
			config = CANVAS_SETUP_TEXT;
			break;
	}

	return step ? config[step] : config;
}
